import { useState, useEffect, useRef } from 'react'
import { Send, Trash2, Bot, User, ChevronDown, ChevronRight, Loader2, FlaskConical, CheckCircle2, RefreshCw, AlertCircle, Link2, Database, Brain } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import { AssistantKB } from '@/hooks/useAssistants'
import { 
  useTestAssistantInfo, 
  useCreateTestAssistant, 
  useUpdateTestAssistant,
  useDeleteTestAssistant,
  useActiveSessionCount
} from '@/hooks/useTestAssistant'
import { useQueryClient } from '@tanstack/react-query'
import { useIntegrations } from '@/hooks/useIntegrations'
import { formatDistanceToNow } from 'date-fns'
import knowledgeApi from '@/lib/knowledge-api'
import { useGlobalMemoryHistory } from '@/hooks/useMemory'
import ReactMarkdown from 'react-markdown'

function getCpUid(): string {
  const match = document.cookie.match(/(?:^|;\s*)cp_uid=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ''
}
import remarkGfm from 'remark-gfm'

interface TestTabProps {
  assistantId: string
  knowledgeBases: AssistantKB[]
}

interface RoutingInfo {
  routed_to_integration?: boolean
  source?: string
  integration_id?: number
}

interface ProcessingStep {
  name: string
  status: 'success' | 'failed' | 'skipped' | 'in_progress'
  timestamp: number
  duration_ms?: number
  details?: Record<string, any>
}

interface FormFieldValidation {
  min_length?: number
  max_length?: number
  pattern?: string
}

interface FormField {
  name: string
  label: string
  type: 'text' | 'tel' | 'email' | 'textarea'
  required: boolean
  placeholder?: string
  validation?: FormFieldValidation
}

interface FormDefinition {
  fields: FormField[]
  submit_label?: string
}

interface TestMessage {
  id: string
  type: 'user' | 'assistant'
  text: string
  timestamp: Date
  source?: string
  chunks?: any[]
  debug?: any
  processing_steps?: ProcessingStep[]
  form?: FormDefinition
  formSubmitted?: boolean
  formDismissed?: boolean
}

interface TestSettings {
  top_k: number
  top_n: number
  similarity_threshold: number
  keywords_similarity_weight: number
  empty_response: boolean
  show_quote: boolean
  prompt: string
}

const DEFAULT_SETTINGS: TestSettings = {
  top_k: 8,
  top_n: 5,
  similarity_threshold: 0.2,
  keywords_similarity_weight: 0.8,
  empty_response: false,
  show_quote: true,
  prompt: '',
}

export function TestTab({ assistantId, knowledgeBases }: TestTabProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  const [messages, setMessages] = useState<TestMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [settings, setSettings] = useState<TestSettings>(DEFAULT_SETTINGS)
  const [selectedKBIds, setSelectedKBIds] = useState<string[]>(
    knowledgeBases.map((kb) => kb.id)
  )
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<number[]>([])
  const [retrievalOpen, setRetrievalOpen] = useState(true)
  const [responseOpen, setResponseOpen] = useState(true)
  const [integrationsOpen, setIntegrationsOpen] = useState(true)
  const [knowledgeBasesOpen, setKnowledgeBasesOpen] = useState(true)
  const [memoryOpen, setMemoryOpen] = useState(true)
  const [cpUid, setCpUid] = useState(getCpUid)

  // Memory for the current browser session (cp_uid cookie)
  const { data: memoryData, refetch: refetchMemory } = useGlobalMemoryHistory({
    cp_uid: cpUid,
    ordering: '-created_at',
    page_size: 50,
  })

  // Fetch integrations for this assistant
  const { data: integrations, isLoading: integrationsLoading } = useIntegrations(assistantId)

  // Get the first selected KB for test assistant operations
  const activeKbId = selectedKBIds[0] || knowledgeBases[0]?.id || ''
  
  // Test assistant hooks for the active KB
  const { data: assistantInfo, isLoading: assistantLoading } = useTestAssistantInfo(activeKbId)
  const createAssistant = useCreateTestAssistant(activeKbId, assistantId)
  const updateAssistant = useUpdateTestAssistant(activeKbId)
  const deleteAssistant = useDeleteTestAssistant(activeKbId)
  const { data: sessionCount } = useActiveSessionCount(activeKbId)

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])
  
  // Initialize prompt with assistant's default prompt when data loads
  useEffect(() => {
    if (assistantInfo?.assistant_prompt && settings.prompt === '') {
      setSettings(prev => ({
        ...prev,
        prompt: assistantInfo.assistant_prompt
      }))
    }
  }, [assistantInfo?.assistant_prompt])
  
  // Select all integrations by default when they load
  useEffect(() => {
    if (integrations && integrations.length > 0 && selectedIntegrationIds.length === 0) {
      setSelectedIntegrationIds(integrations.map((i: any) => i.id))
    }
  }, [integrations])
  
  const handleCreateTestAssistant = async () => {
    try {
      await createAssistant.mutateAsync(settings)
      toast({
        title: 'Test assistant created',
        description: 'You can now test queries with these settings',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to create',
        description: error.response?.data?.error || 'Could not create test assistant',
        variant: 'destructive',
      })
    }
  }
  
  const handleApplySettings = async () => {
    try {
      await updateAssistant.mutateAsync(settings)
      toast({
        title: 'Settings applied',
        description: 'Test assistant updated with new settings',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to apply',
        description: error.response?.data?.error || 'Could not update test assistant',
        variant: 'destructive',
      })
    }
  }
  
  const handleDeleteTestAssistant = async () => {
    if (!confirm('Are you sure you want to delete this test assistant? This action cannot be undone.')) {
      return
    }
    
    try {
      await deleteAssistant.mutateAsync()
      toast({
        title: 'Test assistant deleted',
        description: 'Test assistant has been removed',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to delete',
        description: error.response?.data?.error || 'Could not delete test assistant',
        variant: 'destructive',
      })
    }
  }

  const handleSend = async (formData?: Record<string, string>) => {
    const messageText = formData
      ? Object.entries(formData).map(([k, v]) => `${k}: ${v}`).join(', ')
      : inputValue.trim()
    if (!messageText || isLoading) return
    if (selectedKBIds.length === 0) {
      toast({
        title: 'No knowledge bases selected',
        description: 'Please select at least one knowledge base to test.',
        variant: 'destructive',
      })
      return
    }

    const userMessage: TestMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: formData ? `Submitted: ${messageText}` : messageText,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    if (!formData) setInputValue('')
    setIsLoading(true)

    try {
      // Use the first selected KB for testing (can be extended to support multiple)
      const kbId = selectedKBIds[0]
      const payload: Record<string, any> = {
        query: messageText,
        settings_override: {
          top_k: settings.top_k,
          top_n: settings.top_n,
          similarity_threshold: settings.similarity_threshold,
          keywords_similarity_weight: settings.keywords_similarity_weight,
          show_quote: settings.show_quote,
          empty_response: settings.empty_response,
          prompt: settings.prompt || undefined,
        },
        integration_ids: selectedIntegrationIds,
        use_intent_classification: selectedIntegrationIds.length > 0,
        assistant_id: assistantId,
      }
      if (formData) {
        payload.form_data = formData
      }
      const response = await knowledgeApi.post(`/knowledge/${kbId}/test-query/`, payload)

      const routingInfo: RoutingInfo = {
        routed_to_integration: response.data.routed_to_integration,
        source: response.data.source,
        integration_id: response.data.integration_id,
      }

      const assistantMessage: TestMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: response.data.answer || response.data.response || 'No response received',
        timestamp: new Date(),
        source: response.data.source || 'Chatterpillar',
        chunks: response.data.chunks || [],
        debug: {
          ...response.data.debug,
          routing: routingInfo,
        },
        processing_steps: response.data.processing_steps || [],
        form: response.data.form || undefined,
      }

      // Mark the previous message's form as submitted (if any)
      if (formData) {
        setMessages((prev) => prev.map((m) =>
          m.form && !m.formSubmitted ? { ...m, formSubmitted: true } : m
        ))
      }

      setMessages((prev) => [...prev, assistantMessage])
      queryClient.invalidateQueries({ queryKey: ['active-session-count', activeKbId] })
      // Refetch memory after a short delay to allow async fact extraction to complete
      setTimeout(() => refetchMemory(), 3000)
    } catch (error: any) {
      const errorMessage: TestMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: `Error: ${error.response?.data?.error || error.message || 'Failed to get response'}`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearChat = async () => {
    setMessages([])
    // Also clear backend SRO Redis sessions and sticky integration sessions
    try {
      if (activeKbId) {
        await knowledgeApi.post(`/knowledge/${activeKbId}/test-assistant/clear-session/`)
        queryClient.invalidateQueries({ queryKey: ['active-session-count', activeKbId] })
        // Backend replaced cp_uid cookie — pick up the new value so memory query resets
        setCpUid(getCpUid())
      }
    } catch (err) {
      // Silently ignore - clearing local messages is the primary action
      console.warn('Failed to clear backend session:', err)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const toggleKB = (kbId: string) => {
    setSelectedKBIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId]
    )
  }

  const toggleIntegration = (integrationId: number) => {
    setSelectedIntegrationIds((prev) =>
      prev.includes(integrationId)
        ? prev.filter((id) => id !== integrationId)
        : [...prev, integrationId]
    )
  }

  // Empty state - no KBs
  if (knowledgeBases.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Bot className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No knowledge bases connected</h3>
          <p className="text-sm text-muted-foreground">
            Connect knowledge bases to test this assistant
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-[800px]">
      {/* Left: Chat Interface (60%) */}
      <div className="lg:col-span-3 h-[800px]">
        <div className="relative h-[800px] border rounded-lg bg-card">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 border-b bg-card z-10">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">Test Queries</h3>
              {sessionCount && sessionCount.total > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {sessionCount.total} active session{sessionCount.total !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleClearChat} title="Clear chat and flush active sessions">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Messages Area - Scrollable with padding for header and input */}
          <div className="absolute top-14 bottom-20 left-0 right-0 overflow-auto px-4">
            <div className="space-y-4 py-4">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Send a message to test the assistant
                </div>
              )}
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onFormSubmit={handleSend}
                  onFormCancel={() => {
                    setMessages((prev) => prev.map((m) =>
                      m.id === message.id ? { ...m, formDismissed: true } : m
                    ))
                  }}
                  isLoading={isLoading}
                />
              ))}
              {isLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Floating Input Area - Fixed at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-card">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
              />
              <Button onClick={() => handleSend()} disabled={isLoading || !inputValue.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Settings Panel (40%) */}
      <div className="lg:col-span-2 h-[800px]">
        <Card className="h-full flex flex-col overflow-hidden">
          <CardHeader className="py-3 flex-shrink-0">
            <CardTitle className="text-lg">Test Settings</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto min-h-0 space-y-4">
            {/* KB Selection */}
            <Collapsible open={knowledgeBasesOpen} onOpenChange={setKnowledgeBasesOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 font-medium text-sm w-full">
                {knowledgeBasesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Database className="h-4 w-4" />
                Knowledge Bases
                {selectedKBIds.length > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {selectedKBIds.length} selected
                  </Badge>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-3">
                <div className="space-y-2 max-h-32 overflow-auto">
                  {knowledgeBases.map((kb) => (
                    <div key={kb.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`kb-${kb.id}`}
                        checked={selectedKBIds.includes(kb.id)}
                        onCheckedChange={() => toggleKB(kb.id)}
                      />
                      <label htmlFor={`kb-${kb.id}`} className="text-sm cursor-pointer">
                        {kb.name}
                      </label>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* External Integrations Selection */}
            <Collapsible open={integrationsOpen} onOpenChange={setIntegrationsOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 font-medium text-sm w-full">
                {integrationsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Link2 className="h-4 w-4" />
                External Integrations
                {selectedIntegrationIds.length > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {selectedIntegrationIds.length} selected
                  </Badge>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-3">
                {integrationsLoading ? (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading integrations...
                  </div>
                ) : integrations && integrations.length > 0 ? (
                  <>
                    {selectedIntegrationIds.length > 0 && (
                      <Alert className="py-2">
                        <AlertDescription className="text-xs">
                          Queries will be classified by AI to determine if they should be routed to an external integration or RAGflow.
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {/* Integration List */}
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {integrations.map((integration) => (
                        <div key={integration.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`integration-${integration.id}`}
                            checked={selectedIntegrationIds.includes(integration.id)}
                            onCheckedChange={() => toggleIntegration(integration.id)}
                          />
                          <label 
                            htmlFor={`integration-${integration.id}`} 
                            className="text-sm cursor-pointer flex items-center gap-2"
                          >
                            {integration.name}
                            {!integration.is_active && (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground py-2">
                    No integrations configured. Add integrations in the Integrations tab.
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Test Assistant Status */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">Test Assistant</span>
                </div>
                {assistantInfo?.has_test_assistant ? (
                  <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                    Not Created
                  </Badge>
                )}
              </div>
              
              {assistantLoading ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading...
                </div>
              ) : assistantInfo?.has_test_assistant ? (
                <>
                  {/* Test Assistant Details */}
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ID:</span>
                      <span className="font-mono text-xs">{assistantInfo.test_assistant_id?.slice(0, 16)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">{assistantInfo.test_assistant_name}</span>
                    </div>
                    {assistantInfo.updated_at ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Updated:</span>
                        <span>{formatDistanceToNow(new Date(assistantInfo.updated_at * 1000), { addSuffix: true })}</span>
                      </div>
                    ) : assistantInfo.created_at ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created:</span>
                        <span>{formatDistanceToNow(new Date(assistantInfo.created_at * 1000), { addSuffix: true })}</span>
                      </div>
                    ) : null}
                  </div>
                  
                  {/* Settings Synced Badge */}
                  <div className="flex items-center gap-2">
                    {assistantInfo.settings_synced ? (
                      <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Settings Synced
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Out of Sync
                      </Badge>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={handleApplySettings}
                      disabled={updateAssistant.isPending}
                    >
                      {updateAssistant.isPending ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Applying...</>
                      ) : (
                        <><RefreshCw className="h-4 w-4 mr-2" />Apply Settings</>
                      )}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      className="w-full"
                      onClick={handleDeleteTestAssistant}
                      disabled={deleteAssistant.isPending}
                    >
                      {deleteAssistant.isPending ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                      ) : (
                        <><Trash2 className="h-4 w-4 mr-2" />Delete Test Assistant</>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert>
                    <AlertDescription className="text-xs">
                      Create a test assistant to experiment with different settings.
                    </AlertDescription>
                  </Alert>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={handleCreateTestAssistant}
                    disabled={createAssistant.isPending || !activeKbId}
                  >
                    {createAssistant.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                    ) : (
                      <><FlaskConical className="h-4 w-4 mr-2" />Create Test Assistant</>
                    )}
                  </Button>
                </>
              )}
            </div>

            {/* Retrieval Settings */}
            <Collapsible open={retrievalOpen} onOpenChange={setRetrievalOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 font-medium text-sm w-full">
                {retrievalOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Retrieval Settings
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Label>Top K (chunks to retrieve)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={settings.top_k}
                    onChange={(e) => setSettings({ ...settings, top_k: parseInt(e.target.value) || 5 })}
                    className="w-20"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Top N (chunks in context)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={settings.top_n}
                    onChange={(e) => setSettings({ ...settings, top_n: parseInt(e.target.value) || 3 })}
                    className="w-20"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Similarity Threshold</Label>
                    <span className="text-sm">{settings.similarity_threshold.toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[settings.similarity_threshold]}
                    onValueChange={([v]) => setSettings({ ...settings, similarity_threshold: v })}
                    min={0}
                    max={1}
                    step={0.05}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Keyword Weight</Label>
                    <span className="text-sm">{settings.keywords_similarity_weight.toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[settings.keywords_similarity_weight]}
                    onValueChange={([v]) => setSettings({ ...settings, keywords_similarity_weight: v })}
                    min={0}
                    max={1}
                    step={0.05}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Response Settings */}
            <Collapsible open={responseOpen} onOpenChange={setResponseOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 font-medium text-sm w-full">
                {responseOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Response Settings
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-3">
                <div className="flex items-center justify-between">
                  <Label>Allow Empty Responses</Label>
                  <Switch
                    checked={settings.empty_response}
                    onCheckedChange={(v) => setSettings({ ...settings, empty_response: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Show Source Quotes</Label>
                  <Switch
                    checked={settings.show_quote}
                    onCheckedChange={(v) => setSettings({ ...settings, show_quote: v })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>System Prompt Override</Label>
                  <Textarea
                    value={settings.prompt}
                    onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                    placeholder="Leave empty to use default..."
                    rows={4}
                    className="text-xs"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Session Memory */}
            <Collapsible open={memoryOpen} onOpenChange={setMemoryOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 font-medium text-sm w-full">
                {memoryOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Brain className="h-4 w-4" />
                Session Memory
                {memoryData && memoryData.results.length > 0 && (
                  <Badge variant="secondary" className="ml-auto">
                    {memoryData.count}
                  </Badge>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                {memoryData && memoryData.results.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50 border-b">
                          <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Key</th>
                          <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memoryData.results.map((row, idx) => (
                          <tr key={row.id ?? idx} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                            <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                              {row.category}/{row.key}
                            </td>
                            <td className="py-1.5 px-2 font-medium break-all">{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No memories collected yet. Send messages to extract facts.
                  </p>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-xs"
                  onClick={() => refetchMemory()}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </CollapsibleContent>
            </Collapsible>

            {/* Reset Button */}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setSettings({
                ...DEFAULT_SETTINGS,
                prompt: assistantInfo?.assistant_prompt || DEFAULT_SETTINGS.prompt
              })}
            >
              Reset to Defaults
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function DynamicForm({
  form,
  submitted,
  dismissed,
  onSubmit,
  onCancel,
  isLoading,
}: {
  form: FormDefinition
  submitted: boolean
  dismissed: boolean
  onSubmit: (data: Record<string, string>) => void
  onCancel: () => void
  isLoading: boolean
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries([...form.fields.map((f) => [f.name, '']), ['_comments', '']])
  )
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    for (const field of form.fields) {
      const val = values[field.name]?.trim() ?? ''
      if (field.required && !val) {
        newErrors[field.name] = `${field.label} is required`
        continue
      }
      if (!val) continue
      const v = field.validation
      if (v?.min_length && val.length < v.min_length) {
        newErrors[field.name] = `At least ${v.min_length} characters`
      }
      if (v?.max_length && val.length > v.max_length) {
        newErrors[field.name] = `At most ${v.max_length} characters`
      }
      if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.name] = 'Invalid email'
      }
      if (field.type === 'tel' && !/^[\d\s()+\-]+$/.test(val)) {
        newErrors[field.name] = 'Invalid phone number'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      const data: Record<string, string> = {}
      for (const [k, v] of Object.entries(values)) {
        if (v.trim()) data[k] = v.trim()
      }
      onSubmit(data)
    }
  }

  if (submitted) {
    return (
      <div className="mt-2 px-3 py-2 rounded-md border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
        <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>Form submitted</span>
        </div>
      </div>
    )
  }

  if (dismissed) return null

  // Separate textarea fields (full-width) from inline fields (multi-column)
  const inlineFields = form.fields.filter((f) => f.type !== 'textarea')
  const textareaFields = form.fields.filter((f) => f.type === 'textarea')

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/30 overflow-hidden"
    >
      <div className="px-3 py-1.5 border-b border-indigo-200 dark:border-indigo-800 bg-indigo-100/60 dark:bg-indigo-900/40">
        <span className="text-[11px] font-medium text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
          Please fill in the details
        </span>
      </div>

      <div className="p-3 space-y-2">
        {/* Inline fields in a responsive grid */}
        {inlineFields.length > 0 && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {inlineFields.map((field) => (
              <div key={field.name} className="space-y-0.5">
                <Label htmlFor={`form-${field.name}`} className="text-[11px] font-medium text-muted-foreground">
                  {field.label}
                  {field.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                <Input
                  id={`form-${field.name}`}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={values[field.name]}
                  onChange={(e) => {
                    setValues({ ...values, [field.name]: e.target.value })
                    if (errors[field.name]) setErrors({ ...errors, [field.name]: '' })
                  }}
                  disabled={isLoading}
                  className="h-7 text-xs"
                />
                {errors[field.name] && (
                  <p className="text-[10px] text-destructive">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Textarea fields full-width */}
        {textareaFields.map((field) => (
          <div key={field.name} className="space-y-0.5">
            <Label htmlFor={`form-${field.name}`} className="text-[11px] font-medium text-muted-foreground">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <Textarea
              id={`form-${field.name}`}
              placeholder={field.placeholder}
              value={values[field.name]}
              onChange={(e) => {
                setValues({ ...values, [field.name]: e.target.value })
                if (errors[field.name]) setErrors({ ...errors, [field.name]: '' })
              }}
              disabled={isLoading}
              rows={2}
              className="text-xs resize-none"
            />
            {errors[field.name] && (
              <p className="text-[10px] text-destructive">{errors[field.name]}</p>
            )}
          </div>
        ))}

        {/* Always-present comments field */}
        <div className="space-y-0.5">
          <Label htmlFor="form-_comments" className="text-[11px] font-medium text-muted-foreground">
            Comments / Additional Info
          </Label>
          <Textarea
            id="form-_comments"
            placeholder="Any additional details..."
            value={values['_comments']}
            onChange={(e) => setValues({ ...values, _comments: e.target.value })}
            disabled={isLoading}
            rows={2}
            className="text-xs resize-none"
          />
        </div>
      </div>

      {/* Footer: Cancel left, Submit right */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-indigo-200 dark:border-indigo-800 bg-indigo-100/40 dark:bg-indigo-900/30">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isLoading} className="h-7 text-xs px-3">
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isLoading} className="h-7 text-xs px-4">
          {isLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Submitting...
            </>
          ) : (
            form.submit_label || 'Submit'
          )}
        </Button>
      </div>
    </form>
  )
}

function MessageBubble({
  message,
  onFormSubmit,
  onFormCancel,
  isLoading,
}: {
  message: TestMessage
  onFormSubmit?: (data: Record<string, string>) => void
  onFormCancel?: () => void
  isLoading?: boolean
}) {
  const [showDebug, setShowDebug] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  const isUser = message.type === 'user'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className="flex items-start gap-2">
          {!isUser && (
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Bot className="h-4 w-4" />
            </div>
          )}
          <div>
            <div
              className={`rounded-lg px-4 py-2 ${
                isUser
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted'
              }`}
            >
              {isUser ? (
                <p className="text-sm whitespace-pre-wrap">{message.text}</p>
              ) : (
                <div className="text-sm prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({node, ...props}) => (
                        <a 
                          className="text-primary hover:underline" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          {...props} 
                        />
                      ),
                      p: ({node, ...props}) => <p className="my-1" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside my-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside my-1" {...props} />,
                      li: ({node, ...props}) => <li className="my-0.5" {...props} />,
                    }}
                  >
                    {message.text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
            {!isUser && message.form && onFormSubmit && (
              <DynamicForm
                form={message.form}
                submitted={!!message.formSubmitted}
                dismissed={!!message.formDismissed}
                onSubmit={onFormSubmit}
                onCancel={() => onFormCancel?.()}
                isLoading={!!isLoading}
              />
            )}
            {!isUser && (
              <div className="flex items-center gap-2 mt-1 ml-1">
                <span className="text-xs text-muted-foreground">
                  {message.source || 'Chatterpillar'}
                </span>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(message.timestamp, { addSuffix: true })}
                </span>
              </div>
            )}
          </div>
          {isUser && (
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-primary-foreground" />
            </div>
          )}
        </div>

        {/* Processing Steps Timeline */}
        {!isUser && message.processing_steps && message.processing_steps.length > 0 && (
          <div className="mt-2 ml-10">
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {showSteps ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {message.processing_steps.length} processing steps
            </button>
            {showSteps && (
              <div className="mt-2 border rounded-lg p-3 bg-muted/30">
                <div className="space-y-2">
                  {message.processing_steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        step.status === 'success' ? 'bg-green-500' :
                        step.status === 'failed' ? 'bg-red-500' :
                        step.status === 'skipped' ? 'bg-gray-400' :
                        'bg-yellow-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium">{step.name}</span>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {step.duration_ms !== undefined && (
                              <span>{step.duration_ms}ms</span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                              step.status === 'success' ? 'bg-green-100 text-green-700' :
                              step.status === 'failed' ? 'bg-red-100 text-red-700' :
                              step.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {step.status}
                            </span>
                          </div>
                        </div>
                        {step.details && Object.keys(step.details).length > 0 && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {Object.entries(step.details).map(([key, value]) => (
                              <span key={key} className="mr-2">
                                {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Debug info for assistant messages */}
        {!isUser && message.chunks && message.chunks.length > 0 && (
          <div className="mt-2 ml-10">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {showDebug ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {message.chunks.length} chunks retrieved
            </button>
            {showDebug && (
              <div className="mt-2 space-y-2">
                {message.chunks.map((chunk: any, i: number) => (
                  <div key={i} className="text-xs bg-muted/50 p-2 rounded border">
                    <div className="font-medium mb-1">
                      Score: {chunk.score?.toFixed(3) || 'N/A'}
                    </div>
                    <p className="text-muted-foreground line-clamp-3">
                      {chunk.content || chunk.text || JSON.stringify(chunk)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
